import { motion } from 'framer-motion'
import { Users, Circle, MessageCircle, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const PlayerList = ({ players = [] }) => {
  const { t } = useTranslation(['lobby', 'common'])
  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-green-400'
      case 'away': return 'text-yellow-400'
      case 'busy': return 'text-red-400'
      case 'in-game': return 'text-blue-400'
      default: return 'text-gray-400'
    }
  }

  const getStatusIcon = (status) => {
    const baseClass = "w-2 h-2 rounded-full"
    switch (status) {
      case 'online': return `${baseClass} bg-green-500`
      case 'away': return `${baseClass} bg-yellow-500`
      case 'busy': return `${baseClass} bg-red-500`
      case 'in-game': return `${baseClass} bg-blue-500`
      default: return `${baseClass} bg-gray-500`
    }
  }

  return (
    <div className="glass-effect rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-blue-400" />
        <h3 className="text-lg font-semibold text-white">{t('statistics.onlinePlayers')}</h3>
        <span className="text-sm text-gray-400">({players.length})</span>
      </div>

      {players.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-400 text-sm">{t('noPlayersOnline', { ns: 'common' })}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {players.map((player, index) => (
            <motion.div
              key={player.userId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group"
            >
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {(player.username || player.userId).slice(0, 2).toUpperCase()}
                </div>
                <div className={`absolute -bottom-1 -right-1 ${getStatusIcon(player.status)}`}></div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium truncate">
                    {player.username || player.userId}
                  </span>
                  {player.isGuest && (
                    <span className="px-1.5 py-0.5 bg-gray-700 text-gray-300 text-xs rounded">
                      {t('guest', { ns: 'common' })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Circle className={`w-3 h-3 ${getStatusColor(player.status)}`} />
                  <span className={`text-xs ${getStatusColor(player.status)}`}>
                    {t(player.status, { ns: 'common' })}
                  </span>
                  {player.currentGame && (
                    <span className="text-xs text-gray-400">
                      • {t('in-game', { ns: 'common' })}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-colors"
                  title={t('sendMessage', { ns: 'common' })}
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button
                  className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-400/10 rounded transition-colors"
                  title={t('inviteToGame', { ns: 'common' })}
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PlayerList